import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Archive, RotateCcw, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const REASON = {
  entra_prune: { label: "Left directory", cls: "bg-amber-500/15 text-amber-700" },
  merged_into_website_profile: { label: "Merged duplicate", cls: "bg-blue-500/15 text-blue-700" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function StaffArchive({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/staff-archive").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const restore = async (r) => {
    setBusy(r.id);
    try { await api.post(`/staff-archive/${r.id}/restore`); toast.success(`${r.name} is back in the directory`); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); } finally { setBusy(""); }
  };
  const purge = async (r) => {
    if (!window.confirm(`Permanently delete the archived record for ${r.name}? This cannot be undone.`)) return;
    setBusy(r.id);
    try { await api.delete(`/staff-archive/${r.id}`); toast.success("Archived record deleted"); await load(); }
    catch { toast.error("Delete failed"); } finally { setBusy(""); }
  };

  if (!rows) return <div className="h-32 border border-border rounded-sm animate-pulse bg-secondary/40" data-testid="staff-archive-loading" />;
  const needle = q.trim().toLowerCase();
  const shown = needle ? rows.filter((r) => [r.name, r.email, r.title, r.practice_group].some((v) => (v || "").toLowerCase().includes(needle))) : rows;
  return (
    <div data-testid="staff-archive">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Archive className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Archived staff</span>
        <span className="text-xs text-muted-foreground" data-testid="staff-archive-count">{rows.length} record{rows.length === 1 ? "" : "s"} · moved here by the Office 365 sync when someone leaves the directory or is merged as a duplicate</span>
        <div className="relative ml-auto w-64">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search archive…" className="pl-8 h-8" data-testid="staff-archive-search" />
        </div>
      </div>
      <div className="border border-border rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60"><TableHead>Name</TableHead><TableHead>Title</TableHead><TableHead>Email</TableHead><TableHead>Reason</TableHead><TableHead>Archived</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8" data-testid="staff-archive-empty">{rows.length ? "No matches." : "Nothing archived — every synced person is still in the directory."}</TableCell></TableRow>
            ) : shown.map((r) => {
              const rs = REASON[r.archived_reason] || { label: r.archived_reason || "Archived", cls: "bg-muted text-muted-foreground" };
              return (
                <TableRow key={r.id} data-testid={`archive-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.title || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.email || "—"}</TableCell>
                  <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded-sm font-semibold ${rs.cls}`}>{rs.label}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmt(r.archived_at)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => restore(r)} data-testid={`archive-restore-${r.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
                    <button disabled={busy === r.id} onClick={() => purge(r)} className="ml-3 text-muted-foreground hover:text-destructive align-middle" title="Delete permanently" data-testid={`archive-purge-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
